import {
    ACard,
    Period,
    PeriodSelector,
    Skeleton,
    AChartX
} from "@atiautomacao/ati-ui-library";
import { FileDownloadOutlined, TableChartOutlined, TimerOffOutlined } from "@mui/icons-material";
import { Box, Divider, IconButton, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { addMonths } from "date-fns";
import { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import { AUTHORITIES } from "../../../../Config/Constants";
import { useAppDispatch, useAppSelector } from "../../../../Config/Hooks";
import { RootState } from "../../../../Config/Store";
import { hasPermission } from "../../../../Shared/Auth/AuthenticationUtil";
import DataNotFound from "../../../../Shared/Components/DataNotFoundMessage";
import { getChartXOptions } from "../../../../Utils/ChartXOption";
import { downloadFile } from "../../../../Utils/DownloadUtils";
import { getTrackerAvailability, reset, TrackerAvailability } from "./TrackerAvailabilityReducer";

const MAX_PERIOD_MESSAGE = "O período máximo permitido é de 3 meses";
const INVALID_PERIOD_MESSAGE = "Período inválido";

const normalizeDate = (value?: Date | string | null): Date | undefined => {
    if (!value) {
        return undefined;
    }

    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
};

const formatApiDateTime = (value?: Date | string | null): string | undefined => {
    return normalizeDate(value)?.toJSON().split('.')[0];
};

export const getTrackerAvailabilityPeriodError = (period: Period): string | null => {
    const startDate = normalizeDate(period.fromDateTime);
    const endDate = normalizeDate(period.toDateTime);

    if (!startDate || !endDate || endDate <= startDate) {
        return INVALID_PERIOD_MESSAGE;
    }

    if (endDate > addMonths(startDate, 3)) {
        return MAX_PERIOD_MESSAGE;
    }

    return null;
};

const formatHours = (value?: number): string => {
    return (value ?? 0).toFixed(2);
};

const TrackerAvailabilityChart: FunctionComponent<{ powerPlantId: number;}> = ({powerPlantId}) => {
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const account = useAppSelector(state => state.authentication.account);
    const {trackerAvailability, loading, errorMessage, errorStatus} = useAppSelector((state: RootState) => state.trackerAvailability);

    const isAuthorizedToView = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN,
        AUTHORITIES.VIEW_DASHBOARD,
        AUTHORITIES.VIEW_DASHBOARD_POWER_STATION,
        AUTHORITIES.VIEW_DASHBOARD_POWER_STATION_TRACKER
    ]);
    const isAuthorizedToExportCsv = isAuthorizedToView && hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN,
        AUTHORITIES.VIEW_CSV_EXPORT
    ]);
    const isAuthorizedToExportExcel = isAuthorizedToView && hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN,
        AUTHORITIES.VIEW_EXCEL_EXPORT
    ]);

    const [period, setPeriod] = useState<Period>(() => {
        const toDateTime = new Date();
        const fromDateTime = new Date();
        fromDateTime.setDate(toDateTime.getDate() - 7);
        return {
            groupBy: "week",
            fromDateTime,
            toDateTime
        }
    });

    const periodError = useMemo(() => getTrackerAvailabilityPeriodError(period), [period]);
    const hasData = Array.isArray(trackerAvailability) && trackerAvailability.length > 0;
    const errorDetail = errorStatus
        ? `Erro ${errorStatus}${errorMessage ? `: ${errorMessage}` : ""}`
        : errorMessage;

    useEffect(() => {
        if (!powerPlantId || !isAuthorizedToView || periodError) {
            dispatch(reset());
            return;
        }

        const startDateTime = formatApiDateTime(period.fromDateTime);
        const endDateTime = formatApiDateTime(period.toDateTime);

        if (!startDateTime || !endDateTime) {
            dispatch(reset());
            return;
        }

        dispatch(getTrackerAvailability({powerPlantId, startDateTime, endDateTime}));
    }, [dispatch, isAuthorizedToView, period.fromDateTime, period.toDateTime, periodError, powerPlantId]);

    const handlePeriodChange = (newPeriod: Period) => {
        const validationMessage = getTrackerAvailabilityPeriodError(newPeriod);
        setPeriod(newPeriod);

        if (validationMessage) {
            enqueueSnackbar(validationMessage, { variant: "warning" });
            return;
        }
    };

    const handleDownload = useCallback((format: "csv" | "excel") => {
        const validationMessage = getTrackerAvailabilityPeriodError(period);

        if (validationMessage) {
            enqueueSnackbar(validationMessage, { variant: "warning" });
            return;
        }

        const startDateTime = formatApiDateTime(period.fromDateTime);
        const endDateTime = formatApiDateTime(period.toDateTime);

        if (!startDateTime || !endDateTime) {
            enqueueSnackbar(INVALID_PERIOD_MESSAGE, { variant: "warning" });
            return;
        }

        const extension = format === "csv" ? "csv" : "xlsx";
        downloadFile(
            `/api/power-station/tracker-availability/${powerPlantId}/export/${format}`,
            {startDateTime, endDateTime},
            `tracker-availability.${extension}`,
            undefined,
            () => enqueueSnackbar("Não foi possível exportar os dados.", { variant: "error" })
        );
    }, [enqueueSnackbar, period, powerPlantId]);

    const getMaxDuration = useCallback((): number => {
        const values = trackerAvailability.map((availability: TrackerAvailability) => availability.durationHours ?? 0);
        return values.length > 0 ? Math.max(...values) : 0;
    }, [trackerAvailability]);

    function createOptions(): any {
        const visibleEnd = Math.min(trackerAvailability.length - 1, 29);
        const maxDuration = getMaxDuration();

        return {
            ...getChartXOptions({name:"tracker-availability",date:new Date()}),
            grid: {
                left: 80,
                right: trackerAvailability.length > 30 ? 72 : 28,
                top: 28,
                bottom: 34,
                containLabel: true
            },
            tooltip: {
                trigger: "axis",
                axisPointer: {
                    type: "shadow"
                },
                formatter: (params: any) => {
                    const item = params?.[0];
                    const availability = trackerAvailability[item?.dataIndex];

                    if (!availability) {
                        return "";
                    }

                    return `${availability.trackerCode}<br/>Indisponibilidade: ${formatHours(availability.durationHours)} h<br/>Alarmes: ${availability.alarmCount ?? 0}`;
                }
            },
            xAxis: {
                type: "value",
                name: "Horas",
                min: 0,
                max: maxDuration > 0 ? Number((maxDuration + maxDuration * 0.15).toFixed(2)) : 1,
                nameLocation: "middle",
                nameGap: 28
            },
            yAxis: {
                type: "category",
                data: trackerAvailability.map((availability: TrackerAvailability) => availability.trackerCode),
                inverse: true,
                axisLabel: {
                    overflow: "truncate",
                    width: 90
                }
            },
            dataZoom: trackerAvailability.length > 30 ? [
                {
                    type: "slider",
                    yAxisIndex: 0,
                    orient: "vertical",
                    right: 10,
                    top: 34,
                    bottom: 28,
                    startValue: 0,
                    endValue: visibleEnd
                },
                {
                    type: "inside",
                    yAxisIndex: 0,
                    startValue: 0,
                    endValue: visibleEnd
                }
            ] : [],
            legend: {
                show: false
            },
            series: [{
                name: "Indisponibilidade",
                type: "bar",
                barMaxWidth: 24,
                itemStyle: {
                    color: "#5470C6"
                },
                data: trackerAvailability.map((availability: TrackerAvailability) => Number(formatHours(availability.durationHours)))
            }]
        }
    }

    let content;
    if (!isAuthorizedToView) {
        content = (
            <Box sx={{ height: 350, width: "100%", alignItems: "center", justifyContent: "center", display: "flex" }}>
                <Typography variant="body2" color="text.secondary">Você não está autorizado a visualizar estes dados.</Typography>
            </Box>
        );
    } else if (loading) {
        content = <Skeleton animation="wave" height={350} variant="rounded" width="100%" />;
    } else if (periodError) {
        content = (
            <Box sx={{ height: 350, width: "100%", alignItems: "center", justifyContent: "center", display: "flex" }}>
                <Typography variant="body2" color="warning.main">{periodError}</Typography>
            </Box>
        );
    } else if (errorMessage) {
        content = (
            <Box sx={{ height: 350, width: "100%", alignItems: "center", justifyContent: "center", display: "flex" }}>
                <Typography variant="body2" color="error">Não foi possível carregar os dados{errorDetail ? ` (${errorDetail})` : "."}</Typography>
            </Box>
        );
    } else if (hasData) {
        content = (
            <AChartX
                key={`chart-tracker-availability`}
                option={createOptions()}
                width={'100%'}
                height={350}
                loading={false}
                theme={theme.palette.mode}
            />
        );
    } else {
        content = <DataNotFound boxStyle={{ height: 350, width: '100%' }} />;
    }

    const csvDisabled = loading || !!periodError || !hasData || !isAuthorizedToExportCsv;
    const excelDisabled = loading || !!periodError || !hasData || !isAuthorizedToExportExcel;

    return (
        <ACard
            key={`chart-tracker-availability-card`}
            styleProps={{
                cardStyle: { height: 400, marginBottom: 0, borderRadius: '8px' },
                contentStyle: { padding: '0 16px', height: 350, justifyContent: 'left'},
                titleStyle: { alignItems: 'center', display: 'flex' },
                subTitleStyle: { display: 'none' },
                headerStyle: { flexDirection: 'row', paddingRight:0 },
                headerActionsStyle: { alignItems: 'center', display: 'flex' },
            }}
            title="Indisponibilidade"
            ariaLabel={"dashboard-station-tracker-availability-chart"}
            headerControlsPosition={"header"}
            iconTitle={{
                showIcon: true,
                kind: "mui",
                iconMaterialComponent: TimerOffOutlined,
                iconTitleStyle: {
                    width: 29,
                    height: 32,
                    color: theme.palette.mode === 'dark' ? '#A0D1FF' : '#2D5DE7',
                },
            }}
            headerControls={
                <PeriodSelector
                    styleProps={{base: {flexDirection: "row", display: "flex", alignItems: "center", padding: '8px 0'}}}
                    mode='hybrid'
                    disableHourSelector={true}
                    inputFormat={'dd/MM/yyyy'}
                    period={period}
                    periodInterval={{
                        week: { startInterval: 6, startTime: "00:00:00", endTime: "23:59:59" },
                        month: { startInterval: 30, startTime: "00:00:00", endTime: "23:59:59" },
                        year: { startInterval: 2, endInterval: 0, startTime: "00:00:00", endTime: "23:59:59" },
                    }}
                    groupByOptions={['week', 'month', 'year']}
                    onChange={handlePeriodChange}
                    disabled={loading}
                />
            }
            headerActions={
                <Stack direction="column" spacing={"8px"}>
                    <Stack direction="row" spacing={"4px"} sx={{ alignItems: "center", padding: "0 12px" }}>
                        <Tooltip title="Exportar CSV">
                            <span>
                                <IconButton disabled={csvDisabled} size="small" sx={{borderRadius: '4px'}} onClick={() => handleDownload("csv")}>
                                    <FileDownloadOutlined sx={{ marginRight: '8px' }} />
                                    <Typography variant="body2">CSV</Typography>
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Exportar Excel">
                            <span>
                                <IconButton disabled={excelDisabled} size="small" sx={{borderRadius: '4px'}} onClick={() => handleDownload("excel")}>
                                    <TableChartOutlined sx={{ marginRight: '8px' }} />
                                    <Typography variant="body2">Excel</Typography>
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                    <Divider />
                </Stack>
            }
        >
            {content}
        </ACard>
    );
}

export default TrackerAvailabilityChart;
